import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { QrCode as QrIcon, Copy, ArrowsOut, X, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CheckinQRPage({ bookingId }) {
  const [qr, setQr] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bookingId}/checkin-qr`).then(({ data }) => setQr(data));
  }, [bookingId]);

  if (!qr) return <div className="text-center py-4"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin inline-block" /></div>;

  const payload = `${qr.booking_id}|${qr.token}`;
  const qrUrl = (size) => `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(payload)}`;

  const isCheckedIn = qr.status === "checked_in" || qr.status === "checked_out";
  const statusColor = qr.status === "confirmed" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : qr.status === "checked_in" ? "text-indigo-700 bg-indigo-50 border-indigo-200"
                    : "text-neutral-700 bg-stone border-black/10";

  return (
    <>
      <div className="mt-4 p-6 bg-white rounded-2xl border border-black/10 text-center" data-testid="checkin-qr">
        <p className="text-[10px] tracking-widest uppercase text-gold mb-2">
          <QrIcon size={12} weight="fill" className="inline mr-1" /> Check-in QR
        </p>
        <p className="text-sm text-midnight font-serif mb-1">Show this to the hotel reception</p>
        <p className="text-[11px] text-neutral-500 mb-4">Reception scans this — no typing needed.</p>

        <div className="relative inline-block group cursor-pointer" onClick={() => setFullscreen(true)} data-testid="qr-enlarge-btn">
          <img src={qrUrl(320)} alt="Check-in QR" className="mx-auto rounded-xl border-4 border-white shadow-lg" width={320} height={320} />
          <div className="absolute inset-0 bg-midnight/0 hover:bg-midnight/10 transition-colors rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100">
            <span className="bg-midnight text-bone text-[10px] tracking-widest uppercase px-3 py-1 rounded-full inline-flex items-center gap-1">
              <ArrowsOut size={11} weight="bold" /> Enlarge
            </span>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-neutral-600">
          <span>Or enter token:</span>
          <code className="bg-stone px-2 py-1 rounded font-mono text-midnight tracking-widest">{qr.token}</code>
          <button onClick={() => { navigator.clipboard.writeText(qr.token); toast.success("Token copied"); }}
                  className="p-1 hover:text-gold" data-testid="copy-token" title="Copy token">
            <Copy size={14} />
          </button>
        </div>

        <div className="mt-3">
          <span className={`inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border ${statusColor}`}>
            {isCheckedIn && <CheckCircle size={11} weight="fill" />} Status: {qr.status.replace("_", " ")}
          </span>
        </div>
      </div>

      {/* Fullscreen scannable modal */}
      {fullscreen && (
        <div className="fixed inset-0 z-50 bg-midnight/95 flex items-center justify-center p-6" onClick={() => setFullscreen(false)} data-testid="qr-fullscreen">
          <button onClick={() => setFullscreen(false)} className="absolute top-6 right-6 p-2 text-bone hover:text-gold" data-testid="qr-close-btn">
            <X size={28} weight="bold" />
          </button>
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">Zyro Check-in</p>
            <p className="font-serif text-xl text-midnight mb-5">Reception, please scan</p>
            <img src={qrUrl(500)} alt="Check-in QR fullscreen" className="mx-auto rounded-xl mb-5 w-full max-w-[420px]" />
            <p className="text-xs text-neutral-500">
              Token: <code className="bg-stone px-2 py-1 rounded font-mono text-midnight tracking-widest">{qr.token}</code>
            </p>
            <p className="text-[10px] text-neutral-400 mt-3">Tap anywhere to close</p>
          </div>
        </div>
      )}
    </>
  );
}
